import { INotificationCustomMessage } from 'api/api';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, memo, useRef } from 'react';
import { localized } from 'state/i18n';
import { images } from 'utilities/images';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import { getIcon } from 'view/shared/utils/icon-helper';
import { Spinner } from '../../../spinner/spinner';

interface Props {
	isAdmin: boolean;
	isCountryAdmin: boolean;
	deviceTrackerId?: string;
	deviceTrackerIsActive?: boolean;
	isTrackerDeviceLoading: boolean;
	customMessages: INotificationCustomMessage[];
	onAddTrackerPressed: () => void;
	onCustomMessagePressed: () => void;
}

const buildTrackerButton = (onPress: () => void, disabled: boolean, displayText: string) => {
	return (
		<BiButton
			onClick={onPress}
			disabled={disabled}
			colorTheme="org-white"
			containerClassName="details-device-card margin-right-14px"
		>
			<div className="flex-direction-column flex-center-column details-device-card-fixed">
				{disabled ? (
					<Spinner
						containerClassName="flex-center-column"
						shouldOverlay={false}
						spinnerSize="spinner-container-half-size"
						useMarginsForCorrectingCentering={false}
					/>
				) : (
					<>
						<img src={images.tracker} alt={displayText} />
						<span className="text-bold">{displayText}</span>
					</>
				)}
			</div>
		</BiButton>
	);
};

const buildAddTrackerButton = (onPress: () => void, displayText: string) => {
	return (
		<div className="p-col-6 details-device-card">
			<BiButton
				onClick={onPress}
				colorTheme="org-white"
				containerClassName="details-device-card"
				className="bi-button-big-with-soft-rounded-corners bi-button-big-border-spacing bi-button-org-white undefined"
			>
				<div className="flex-center-column">
					<div className="flex-direction-column flex-center-column details-device-card-fixed">
						<img src={images.tracker} alt={displayText} />
						<span className="text-bold">{displayText}</span>
					</div>
				</div>
			</BiButton>
		</div>
	);
};

export const DeviceContent: FC<Props> = memo(props => {
	const {
		isAdmin,
		isCountryAdmin,
		deviceTrackerId,
		deviceTrackerIsActive,
		isTrackerDeviceLoading,
		customMessages,
		onAddTrackerPressed,
		onCustomMessagePressed,
	} = props;

	const overlayPanelRef = useRef<OverlayPanel>(null);

	const handleAddNewDeviceToggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		overlayPanelRef.current?.toggle(e);
	};

	const trackerButton =
		isCountryAdmin &&
		deviceTrackerIsActive &&
		deviceTrackerId &&
		buildTrackerButton(onAddTrackerPressed, isTrackerDeviceLoading, localized('Tracker'));

	const addTrackerButton = !deviceTrackerId && buildAddTrackerButton(onAddTrackerPressed, localized('Tracker'));

	return (
		<>
			{trackerButton}
			{isCountryAdmin && !deviceTrackerId && (
				<div>
					<div onClick={handleAddNewDeviceToggle}>
						<BiButton colorTheme="org-white" containerClassName="details-device-card margin-right-14px">
							<div className="flex-direction-column flex-center-column btn-content">
								{getIcon('plus', 43)}
								<span className="text-bold">{localized('AddNewDevice')}</span>
							</div>
						</BiButton>
					</div>
					<OverlayPanel ref={overlayPanelRef}>
						<div className="p-grid button-border">
							{addTrackerButton}
							<br />
						</div>
					</OverlayPanel>
				</div>
			)}

			{isAdmin && customMessages && customMessages.length > 0 && (
				<BiButton
					onClick={onCustomMessagePressed}
					colorTheme="org-white"
					containerClassName="details-device-card margin-right-14px"
				>
					<div className="flex-direction-column flex-center-column btn-content">
						<img src={images.message} alt={localized('CustomMessages')} />
						<span className="text-bold">{localized('CustomMessages')}</span>
					</div>
				</BiButton>
			)}
		</>
	);
});
